// useTeamNotifications — Story 93
// Subscribes to users/{uid}/notifications collection.
// Returns unread count, sorted notification list (max 20, newest first),
// and a markAllRead() helper.

import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  writeBatch,
  doc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { RootState } from "../setup/redux/Store";

export type TeamNotificationType =
  | "handoff_ready"
  | "step_assigned"
  | "project_shared"
  | "team_invite";

export interface TeamNotification {
  id: string;
  type: TeamNotificationType;
  message: string;
  read: boolean;
  createdAt: string; // ISO string
  expiresAt: string; // ISO string — 14 days after creation
  projectID?: string;
  stepNumber?: number;
  actorEmail?: string;
  linkPath?: string; // relative path to navigate to on click
}

export function useTeamNotifications() {
  const user = useSelector((state: RootState) => state.auth.user) as any;
  const uid = user?.uid as string | undefined;

  const [notifications, setNotifications] = useState<TeamNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    const now = new Date().toISOString();
    const q = query(
      collection(db, "users", uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all: TeamNotification[] = [];
        snap.forEach((d) => {
          const data = d.data();
          // Client-side expiry filter — skip expired notifications
          if (data.expiresAt && data.expiresAt < now) return;
          all.push({ id: d.id, ...data } as TeamNotification);
        });
        setNotifications(all);
        setLoading(false);
      },
      () => {
        // Firestore listener error (e.g. no rules yet) — fail silently
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  const markAllRead = useCallback(async () => {
    if (!uid) return;
    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach((n) => {
      batch.update(doc(db, "users", uid, "notifications", n.id), { read: true });
    });
    await batch.commit();
  }, [uid, notifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return { notifications, unreadCount, loading, markAllRead };
}

// ─── Write helper (used by client-side callers) ───────────────────────────────

// Writes a notification document to users/{targetUID}/notifications/{id}.
// Fire-and-forget — caller should not await.
export async function writeNotification(
  targetUID: string,
  id: string,
  payload: Omit<TeamNotification, "id" | "expiresAt">
): Promise<void> {
  try {
    const { setDoc: fsSetDoc } = await import("firebase/firestore");
    const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
    const notifRef = doc(db, "users", targetUID, "notifications", id);
    await fsSetDoc(
      notifRef,
      { ...payload, expiresAt },
      { merge: true }
    );
  } catch {
    // Non-blocking — notification write failures should not impact UX
  }
}
